export const DEFAULT_ENDPOINTS = {
  test: "https://testapp.codeffect.top/statistic/api/v2/poke/web_aggregation",
  prod: "https://statistic.codeffect.top/api/v2/poke/web_aggregation",
  prod_jp: "https://api.hkcodebox.com/cf-statistic/api/v2/poke/web_aggregation",
} as const;

export const DEFAULT_EVENT_ALLOW_LIST = {
  web_page_view: ["surface", "page", "path", "source"],
  web_click: ["surface", "page", "path", "source", "module", "position", "button", "target"],
} as const;

export type TrackerEnv = keyof typeof DEFAULT_ENDPOINTS;
export type EventPropertyValue = string | number | boolean;
export type EventProperties = Record<string, EventPropertyValue>;
export type EventPropertiesInput = Record<string, unknown>;
export type EventAllowList = Record<string, readonly string[]>;

export interface WebAggregationEvent {
  event_name: string;
  event_at: number;
  properties: EventProperties;
}

export interface WebAggregationPayload {
  biz: string;
  datas: WebAggregationEvent[];
}

export interface TransportContext {
  useBeacon: boolean;
}

export type TrackerTransport = (
  endpoint: string,
  payload: WebAggregationPayload,
  context: TransportContext,
) => Promise<void> | void;

export interface FlushResult {
  ok: boolean;
  sent: boolean;
  count: number;
  error?: unknown;
}

export interface TrackOptions {
  eventAt?: number;
}

export interface DocumentLike {
  addEventListener(type: string, handler: (event: { target?: unknown }) => void): void;
  removeEventListener(type: string, handler: (event: { target?: unknown }) => void): void;
}

export interface WindowLike {
  document?: DocumentLike;
  location?: {
    pathname?: string;
    search?: string;
  };
  navigator?: {
    sendBeacon?: (url: string, data: BodyInit) => boolean;
  };
  fetch?: typeof fetch;
  addEventListener?: (type: string, handler: () => void) => void;
  removeEventListener?: (type: string, handler: () => void) => void;
  setInterval?: (handler: () => void, timeout?: number) => number;
  clearInterval?: (handle: number) => void;
}

export interface WebAggregationTrackerConfig {
  biz: string;
  env?: TrackerEnv;
  endpoint?: string;
  batch?: boolean;
  batchSize?: number;
  flushInterval?: number;
  maxQueueSize?: number;
  debug?: boolean;
  autoClick?: boolean;
  allowList?: EventAllowList;
  strictEvents?: boolean;
  filterProperties?: boolean;
  commonProperties?: EventPropertiesInput | (() => EventPropertiesInput);
  contextProvider?: () => EventPropertiesInput;
  now?: () => number;
  transport?: TrackerTransport;
  win?: WindowLike;
}

interface NormalizedConfig {
  biz: string;
  endpoint: string;
  batch: boolean;
  batchSize: number;
  flushInterval: number;
  maxQueueSize: number;
  debug: boolean;
  autoClick: boolean;
  strictEvents: boolean;
  filterProperties: boolean;
  commonProperties: EventPropertiesInput | (() => EventPropertiesInput) | undefined;
  contextProvider: (() => EventPropertiesInput) | undefined;
  now: () => number;
  transport: TrackerTransport | undefined;
  win: WindowLike | undefined;
}

interface TrackElementLike {
  dataset?: Record<string, string | undefined>;
  closest?: (selector: string) => TrackElementLike | null;
}

const TRACK_CLICK_SELECTOR = "[data-wa-click]";
const DECLARATIVE_PROPS_KEYS = new Set(["waProps", "waProperties"]);
const DECLARATIVE_PROP_PREFIX = "waProp";

function getRuntimeWindow(): WindowLike | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window;
}

function isPlainPropertyValue(value: unknown): value is EventPropertyValue {
  if (typeof value === "string" || typeof value === "boolean") {
    return true;
  }
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeConfig(config: WebAggregationTrackerConfig): NormalizedConfig {
  const biz = config.biz.trim();
  if (!biz) {
    throw new Error("web aggregation tracker requires a non-empty biz");
  }

  const env = config.env ?? "prod";
  const endpoint = config.endpoint ?? DEFAULT_ENDPOINTS[env];

  return {
    biz,
    endpoint,
    batch: config.batch ?? true,
    batchSize: Math.max(1, config.batchSize ?? 10),
    flushInterval: Math.max(0, config.flushInterval ?? 5000),
    maxQueueSize: Math.max(1, config.maxQueueSize ?? 100),
    debug: config.debug ?? false,
    autoClick: config.autoClick ?? false,
    strictEvents: config.strictEvents ?? true,
    filterProperties: config.filterProperties ?? true,
    commonProperties: config.commonProperties,
    contextProvider: config.contextProvider,
    now: config.now ?? Date.now,
    transport: config.transport,
    win: config.win ?? getRuntimeWindow(),
  };
}

function mergeDefinedProperties(...sources: EventPropertiesInput[]): EventPropertiesInput {
  const result: EventPropertiesInput = {};
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (value !== undefined && value !== null) {
        result[key] = value;
      }
    }
  }
  return result;
}

function resolveProperties(
  source: EventPropertiesInput | (() => EventPropertiesInput) | undefined,
): EventPropertiesInput {
  if (!source) {
    return {};
  }
  if (typeof source === "function") {
    return source();
  }
  return source;
}

function trimQueue(queue: WebAggregationEvent[], maxQueueSize: number): WebAggregationEvent[] {
  if (queue.length <= maxQueueSize) {
    return queue;
  }
  return queue.slice(queue.length - maxQueueSize);
}

function camelToSnake(value: string): string {
  return value
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
}

function datasetPropKeyToPropertyName(datasetKey: string): string {
  const suffix = datasetKey.slice(DECLARATIVE_PROP_PREFIX.length);
  if (!suffix) {
    return "";
  }
  return camelToSnake(suffix);
}

function parseJsonProperties(raw: string | undefined): EventPropertiesInput {
  if (!raw) {
    return {};
  }

  try {
    const value = JSON.parse(raw) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {};
    }
    return value as EventPropertiesInput;
  } catch {
    return {};
  }
}

function getDeclarativeProperties(dataset: Record<string, string | undefined>): EventPropertiesInput {
  const properties = mergeDefinedProperties(
    parseJsonProperties(dataset.waProps),
    parseJsonProperties(dataset.waProperties),
  );

  for (const [key, value] of Object.entries(dataset)) {
    if (DECLARATIVE_PROPS_KEYS.has(key) || !key.startsWith(DECLARATIVE_PROP_PREFIX)) {
      continue;
    }

    const propertyName = datasetPropKeyToPropertyName(key);
    if (propertyName && value !== undefined) {
      properties[propertyName] = value;
    }
  }

  return properties;
}

export class WebAggregationTracker {
  private config: NormalizedConfig | undefined;
  private allowList: EventAllowList = DEFAULT_EVENT_ALLOW_LIST;
  private queue: WebAggregationEvent[] = [];
  private flushTimer: number | undefined;
  private clickHandler: ((event: { target?: unknown }) => void) | undefined;
  private pagehideHandler: (() => void) | undefined;

  constructor(config?: WebAggregationTrackerConfig) {
    if (config) {
      this.init(config);
    }
  }

  init(config: WebAggregationTrackerConfig): this {
    this.destroy();

    this.config = normalizeConfig(config);
    this.allowList = {
      ...DEFAULT_EVENT_ALLOW_LIST,
      ...(config.allowList ?? {}),
    };

    this.startLifecycleHooks();

    if (this.config.autoClick) {
      this.bindAutoClick();
    }

    return this;
  }

  track(eventName: string, properties: EventPropertiesInput = {}, options: TrackOptions = {}): boolean {
    const config = this.requireConfig();
    const normalizedEventName = eventName.trim();
    if (!normalizedEventName) {
      return false;
    }

    const allowedKeys = this.allowList[normalizedEventName];
    if (config.strictEvents && !allowedKeys) {
      this.debug(`drop unsupported event: ${normalizedEventName}`);
      return false;
    }

    const mergedProperties = mergeDefinedProperties(
      this.getContextProperties(),
      resolveProperties(config.commonProperties),
      properties,
    );

    const event: WebAggregationEvent = {
      event_name: normalizedEventName,
      event_at: Math.trunc(options.eventAt ?? config.now()),
      properties: this.sanitizeProperties(mergedProperties, allowedKeys),
    };

    this.queue.push(event);
    this.queue = trimQueue(this.queue, config.maxQueueSize);

    if (!config.batch || this.queue.length >= config.batchSize) {
      void this.flush();
    }

    return true;
  }

  async flush(options: { useBeacon?: boolean } | boolean = {}): Promise<FlushResult> {
    const config = this.requireConfig();
    if (this.queue.length === 0) {
      return { ok: true, sent: false, count: 0 };
    }

    const useBeacon = typeof options === "boolean" ? options : options.useBeacon ?? false;
    const datas = this.queue.splice(0, this.queue.length);
    const payload: WebAggregationPayload = {
      biz: config.biz,
      datas,
    };

    try {
      await this.send(payload, useBeacon);
      return { ok: true, sent: true, count: datas.length };
    } catch (error) {
      this.queue = trimQueue(datas.concat(this.queue), config.maxQueueSize);
      this.debug("send failed", error);
      return { ok: false, sent: false, count: 0, error };
    }
  }

  setCommonProperties(properties: EventPropertiesInput | (() => EventPropertiesInput)): void {
    const config = this.requireConfig();
    config.commonProperties = properties;
  }

  getQueueSize(): number {
    return this.queue.length;
  }

  destroy(): void {
    if (this.config?.win && this.flushTimer !== undefined) {
      this.config.win.clearInterval?.(this.flushTimer);
    }
    if (this.config?.win && this.pagehideHandler) {
      this.config.win.removeEventListener?.("pagehide", this.pagehideHandler);
    }
    if (this.config?.win?.document && this.clickHandler) {
      this.config.win.document.removeEventListener("click", this.clickHandler);
    }

    this.flushTimer = undefined;
    this.pagehideHandler = undefined;
    this.clickHandler = undefined;
    this.queue = [];
    this.config = undefined;
  }

  private requireConfig(): NormalizedConfig {
    if (!this.config) {
      throw new Error("web aggregation tracker is not initialized");
    }
    return this.config;
  }

  private getContextProperties(): EventPropertiesInput {
    const config = this.requireConfig();
    if (config.contextProvider) {
      return config.contextProvider();
    }
    return {};
  }

  private sanitizeProperties(
    properties: EventPropertiesInput,
    allowedKeys: readonly string[] | undefined,
  ): EventProperties {
    const config = this.requireConfig();
    const keys = config.filterProperties && allowedKeys ? allowedKeys : Object.keys(properties);
    const result: EventProperties = {};

    for (const key of keys) {
      const value = properties[key];
      if (isPlainPropertyValue(value)) {
        result[key] = value;
      }
    }

    return result;
  }

  private startLifecycleHooks(): void {
    const config = this.requireConfig();
    if (config.batch && config.flushInterval > 0 && config.win?.setInterval) {
      this.flushTimer = config.win.setInterval(() => {
        void this.flush();
      }, config.flushInterval);
    }

    if (config.win?.addEventListener) {
      this.pagehideHandler = () => {
        void this.flush({ useBeacon: true });
      };
      config.win.addEventListener("pagehide", this.pagehideHandler);
    }
  }

  private bindAutoClick(): void {
    const config = this.requireConfig();
    if (!config.win?.document) {
      this.debug("autoClick is enabled, but document is unavailable");
      return;
    }

    this.clickHandler = (event) => {
      const element = this.findTrackElement(event.target);
      if (!element) {
        return;
      }

      const dataset = element.dataset ?? {};
      const eventName = dataset.waEvent?.trim();
      if (!eventName) {
        this.debug("drop declarative click without data-wa-event");
        return;
      }

      this.track(eventName, getDeclarativeProperties(dataset));
    };

    config.win.document.addEventListener("click", this.clickHandler);
  }

  private findTrackElement(target: unknown): TrackElementLike | null {
    if (!target || typeof target !== "object") {
      return null;
    }

    const element = target as TrackElementLike;
    if (typeof element.closest !== "function") {
      return null;
    }
    return element.closest(TRACK_CLICK_SELECTOR);
  }

  private async send(payload: WebAggregationPayload, useBeacon: boolean): Promise<void> {
    const config = this.requireConfig();
    if (config.transport) {
      await config.transport(config.endpoint, payload, { useBeacon });
      return;
    }

    if (useBeacon && this.sendWithBeacon(payload)) {
      return;
    }

    await this.sendWithFetch(payload);
  }

  private sendWithBeacon(payload: WebAggregationPayload): boolean {
    const config = this.requireConfig();
    const sendBeacon = config.win?.navigator?.sendBeacon;
    if (!sendBeacon) {
      return false;
    }

    const body = JSON.stringify(payload);
    const blob = new Blob([body], { type: "application/json" });
    return sendBeacon(config.endpoint, blob);
  }

  private async sendWithFetch(payload: WebAggregationPayload): Promise<void> {
    const config = this.requireConfig();
    const fetcher = config.win?.fetch
      ? config.win.fetch.bind(config.win)
      : globalThis.fetch?.bind(globalThis);

    if (!fetcher) {
      throw new Error("fetch is unavailable; provide a custom transport");
    }

    const response = await fetcher(config.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      credentials: "omit",
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`web aggregation request failed with status ${response.status}`);
    }
  }

  private debug(message: string, extra?: unknown): void {
    if (!this.config?.debug) {
      return;
    }
    if (extra === undefined) {
      console.warn(`[web-aggregation-tracker] ${message}`);
    } else {
      console.warn(`[web-aggregation-tracker] ${message}`, extra);
    }
  }
}

export function createTracker(config: WebAggregationTrackerConfig): WebAggregationTracker {
  return new WebAggregationTracker(config);
}

export const webAggregationTracker = new WebAggregationTracker();

export function init(config: WebAggregationTrackerConfig): WebAggregationTracker {
  return webAggregationTracker.init(config);
}

export function track(
  eventName: string,
  properties?: EventPropertiesInput,
  options?: TrackOptions,
): boolean {
  return webAggregationTracker.track(eventName, properties, options);
}

export function flush(options?: { useBeacon?: boolean } | boolean): Promise<FlushResult> {
  return webAggregationTracker.flush(options);
}
